import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Table, Button, Input, Select, Tag, Space, Typography,
  Modal, Form, Switch, message, Popconfirm, Row, Col,
  Alert, Divider, Badge, Tabs, Radio, Tooltip,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, SearchOutlined,
  ThunderboltOutlined, HomeOutlined, ReloadOutlined, CheckCircleOutlined,
  SettingOutlined, SaveOutlined, LinkOutlined, GlobalOutlined,
} from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const API = '/api/v1/jj';
const h = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

const getCategoryConfig = (t) => ({
  bedrooms:      { label: `🛏 ${t('kw.bedrooms','Bedrooms')}`, color: 'blue',    hint: 'value = number (0,1,2,3...)' },
  area:          { label: `📍 ${t('kw.area','Area')}`,         color: 'purple',  hint: 'value = area name (Thonglor, Asok...)' },
  intent:        { label: `🎯 ${t('kw.intent','Intent')}`,     color: 'green',   hint: 'value = rent or buy' },
  property_type: { label: `🏠 ${t('kw.propertyType','Type')}`, color: 'orange',  hint: 'value = condo, house, apartment...' },
  budget:        { label: `💰 ${t('kw.budget','Budget')}`,     color: 'gold',    hint: 'value = max price (THB)' },
});

// ── HouseAI Config Panel ──────────────────────────────────────────────────────
function HouseAIConfigPanel() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [frontendUrl, setFrontendUrl] = useState('/house-ai-rental');
  const PLATFORM = 'https://marcom.comboware.tw';

  useEffect(() => {
    fetch(`${API}/settings/houseai`, { headers: h() })
      .then(r => r.json())
      .then(d => {
        setConfig(d);
        setFrontendUrl(d.houseai_frontend_url || '/house-ai-rental');
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/settings/houseai`, {
        method: 'POST', headers: h(),
        body: JSON.stringify({ houseai_frontend_url: frontendUrl }),
      }).then(r => r.json());
      if (res.ok) message.success(t('kw.houseaiSaved', 'HouseAI source saved'));
      else message.error(t('kw.saveFailed', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = `${PLATFORM}${frontendUrl}`;

  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <SettingOutlined style={{ color: '#1890ff', fontSize: 18 }} />
        <Text strong style={{ fontSize: 15 }}>{t('kw.houseaiTitle', 'HouseAI Property Source Settings')}</Text>
      </div>

      <Alert
        message={t('kw.houseaiDesc', 'Select which HouseAI page to use for property matching. When customer keywords are triggered, the system will search properties from this source and push results via Line Bot.')}
        type="info" style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>{t('kw.selectSource', 'Select matching source page:')}</Text>
        <Radio.Group
          value={frontendUrl}
          onChange={e => setFrontendUrl(e.target.value)}
        >
          <Space direction="vertical" size={12}>
            <Radio value="/house-ai-rental">
              <div>
                <Text strong>🏠 Property Search (/house-ai-rental)</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('kw.rentalDesc', 'Embedded search UI showing FB crawled properties')}
                </Text>
                <br />
                <Text code style={{ fontSize: 11 }}>{PLATFORM}/house-ai-rental</Text>
              </div>
            </Radio>
            <Radio value="/house-ai">
              <div>
                <Text strong>📊 HOUSE AI Dashboard (/house-ai)</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('kw.dashboardDesc', 'Full management dashboard with crawler, listings, stats')}
                </Text>
                <br />
                <Text code style={{ fontSize: 11 }}>{PLATFORM}/house-ai</Text>
              </div>
            </Radio>
          </Space>
        </Radio.Group>
      </div>

      <div style={{ background: '#f6f6f6', padding: 12, borderRadius: 8, marginBottom: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>{t('kw.currentUrl', 'Current URL:')}</Text>
        <br />
        <Space>
          <Text code style={{ fontSize: 13 }}>{previewUrl}</Text>
          <Tooltip title={t('kw.openPreview', 'Open preview in new window')}>
            <Button
              size="small" type="link" icon={<LinkOutlined />}
              onClick={() => window.open(previewUrl, '_blank')}
            >
              {t('kw.preview', 'Preview')}
            </Button>
          </Tooltip>
        </Space>
      </div>

      <Button
        type="primary" icon={<SaveOutlined />}
        loading={saving} onClick={handleSave}
      >
        {t('kw.saveSettings', 'Save Settings')}
      </Button>
    </Card>
  );
}


export default function JJKeywordsPage() {
  const { t } = useTranslation();
  const CATEGORY_CONFIG = getCategoryConfig(t);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [testText, setTestText] = useState('');
  const [testLang, setTestLang] = useState('english');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    const data = await fetch(`${API}/keywords`, { headers: h() }).then(r => r.json());
    setKeywords(data.keywords || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    const vals = await form.validateFields();
    await fetch(`${API}/keywords`, {
      method: 'POST', headers: h(), body: JSON.stringify(vals),
    });
    message.success(t('kw.keywordAdded', 'Keyword added'));
    form.resetFields();
    setAddModal(false);
    load();
  };

  const handleToggle = async (id, active) => {
    await fetch(`${API}/keywords/${id}`, {
      method: 'PATCH', headers: h(), body: JSON.stringify({ is_active: active }),
    });
    load();
  };

  const handleDelete = async (id) => {
    await fetch(`${API}/keywords/${id}`, { method: 'DELETE', headers: h() });
    message.success(t('kw.deleted', 'Deleted'));
    load();
  };

  const handleTest = async () => {
    if (!testText.trim()) return;
    setTesting(true);
    const res = await fetch(`${API}/keywords/test`, {
      method: 'POST', headers: h(),
      body: JSON.stringify({ text: testText, language: testLang }),
    }).then(r => r.json());
    setTestResult(res);
    setTesting(false);
  };

  // Group by category
  const byCategory = {};
  keywords.forEach(kw => {
    if (!byCategory[kw.category]) byCategory[kw.category] = [];
    byCategory[kw.category].push(kw);
  });

  const cols = [
    {
      title: t('kw.keyword', 'Keyword'), dataIndex: 'keyword', width: 140,
      render: (v, r) => (
        <Text strong style={{ fontSize: 14, color: r.is_active ? '#333' : '#bbb' }}>{v}</Text>
      ),
    },
    {
      title: t('kw.category', 'Category'), dataIndex: 'category', width: 110,
      render: v => {
        const c = CATEGORY_CONFIG[v] || { label: v, color: 'default' };
        return <Tag color={c.color}>{c.label}</Tag>;
      },
    },
    {
      title: t('kw.value', 'Value'), dataIndex: 'value', width: 100,
      render: v => v ? <Tag>{v}</Tag> : '-',
    },
    {
      title: t('kw.language', 'Language'), dataIndex: 'language', width: 70,
      render: v => ({ zh: `🇹🇼 ${t('kw.langZh','Chinese')}`, en: '🇬🇧 EN', th: '🇹🇭 Thai', any: `🌐 ${t('kw.anyLang','Any')}` }[v] || v),
    },
    {
      title: t('kw.active', 'Active'), dataIndex: 'is_active', width: 70,
      render: (v, r) => (
        <Switch checked={v} size="small" onChange={active => handleToggle(r.id, active)} />
      ),
    },
    {
      title: '', width: 50,
      render: (_, r) => (
        <Popconfirm title={t('common.confirmDelete','Confirm delete?')} onConfirm={() => handleDelete(r.id)} okText={t('common.delete','Delete')} cancelText={t('common.cancel','Cancel')} okButtonProps={{ danger: true }}>
          <Button danger type="text" size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {`🔑 ${t('kw.pageTitle', 'JJ Property Keywords')}`}
          </Title>
          <Text type="secondary">
            {t('kw.pageDesc', 'When customer messages contain these keywords, the system automatically searches HouseAI and sends matching properties.')}
          </Text>
        </div>
        <Space>
          <Button icon={<PlusOutlined />} type="primary" onClick={() => setAddModal(true)}>
            {t('kw.addKeyword', 'Add Keyword')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={load} />
        </Space>
      </div>

      {/* HouseAI Source Config */}
      <HouseAIConfigPanel />

      <Alert
        message={
          <span>
            ⚡ <strong>{t('kw.autoMatchFlow', 'Auto Matching Flow:')}</strong>
            {t('kw.autoMatchDesc', 'Customer message → Keyword detection → Extract requirements → Search HouseAI → Auto-reply via Line Bot')}
          </span>
        }
        type="info" style={{ marginBottom: 16 }}
      />

      <Tabs defaultActiveKey="table">
        <TabPane tab={`📋 ${t('kw.keywordList', 'Keywords')}`} key="table">
          <Row gutter={16} style={{ marginBottom: 12 }}>
            {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
              const count = (byCategory[key] || []).length;
              const active = (byCategory[key] || []).filter(k => k.is_active).length;
              return (
                <Col key={key} span={4}>
                  <Card size="small" style={{ borderTop: `3px solid`, borderTopColor: { blue:'#1890ff', purple:'#722ed1', green:'#52c41a', orange:'#fa8c16', gold:'#faad14' }[cfg.color] }}>
                    <div style={{ fontSize: 12 }}>{cfg.label}</div>
                    <div><Text strong>{active}</Text><Text type="secondary">{`/${count} ${t('kw.enabled', 'on')}`}</Text></div>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <Table
            dataSource={keywords}
            columns={cols}
            rowKey="id"
            loading={loading}
            size="small"
            pagination={{ pageSize: 20, showTotal: total => `${total} ${t('kw.keywords', 'keywords')}` }}
            rowClassName={r => r.is_active ? '' : 'opacity-50'}
          />
        </TabPane>

        <TabPane tab={`🧪 ${t('kw.testKeywords', 'Test')}`} key="test">
          <Card>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {t('kw.testDesc', 'Enter a sample customer message to test keyword detection and property matching:')}
            </Text>
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={14}>
                <Input.TextArea
                  value={testText}
                  onChange={e => setTestText(e.target.value)}
                  rows={3}
                  placeholder={t('kw.testPlaceholder','e.g. I want a 3BR condo near Thonglor, budget 15000–30000 THB')}
                />
              </Col>
              <Col span={6}>
                <Select value={testLang} onChange={setTestLang} style={{ width: '100%', marginBottom: 8 }}>
                  <Option value="english">🇬🇧 English</Option>
                  <Option value="chinese">🇹🇼 {t('kw.langZh','Chinese')}</Option>
                  <Option value="thai">🇹🇭 ภาษาไทย</Option>
                </Select>
                <Button
                  type="primary" block icon={<ThunderboltOutlined />}
                  loading={testing} onClick={handleTest}
                >
                  {t('kw.testMatch','Test Match')}
                </Button>
              </Col>
            </Row>

            {/* Quick test examples */}
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{t('kw.quickExamples','Quick examples:')}</Text>
              {[
                'I need 2 bedroom condo near Thonglor, budget 15000 THB',
                'Looking for 2 bedroom condo near BTS Asok',
                'ต้องการเช่าคอนโด 1 ห้องนอน แถว Phromphong',
              ].map((ex, i) => (
                <Tag key={i} style={{ cursor: 'pointer', marginTop: 4 }}
                  onClick={() => setTestText(ex)}>
                  {ex.slice(0, 30)}...
                </Tag>
              ))}
            </div>

            {testResult && (
              <div>
                <Divider>{t('kw.detectionResult', 'Detection Result')}</Divider>
                {testResult.detected && Object.keys(testResult.detected).length > 0 ? (
                  <>
                    <Row gutter={12} style={{ marginBottom: 12 }}>
                      <Col span={12}>
                        <Text strong>{t('kw.detected','Detected keywords:')}</Text>
                        <div style={{ marginTop: 6 }}>
                          {(testResult.detected.matched_keywords || []).map(kw => (
                            <Tag key={kw} color="blue" style={{ marginBottom: 4 }}>{kw}</Tag>
                          ))}
                        </div>
                        <div style={{ marginTop: 8 }}>
                          {testResult.detected.bedrooms != null && <Tag color="purple">🛏 {testResult.detected.bedrooms} {t('kw.bedLabel', 'bed')}</Tag>}
                          {testResult.detected.area && <Tag color="green">📍 {testResult.detected.area}</Tag>}
                          {testResult.detected.intent && <Tag color="orange">🎯 {testResult.detected.intent}</Tag>}
                        </div>
                      </Col>
                      <Col span={12}>
                        <Text strong>{t('kw.houseaiMatch','HouseAI matches:')}</Text>
                        <Badge count={testResult.properties_found} style={{ marginLeft: 8, background: '#52c41a' }} />
                      </Col>
                    </Row>
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                      {t('kw.previewMsg','Preview Line message to customer:')}
                    </Text>
                    <pre style={{
                      background: '#f6f0ff', padding: 16, borderRadius: 8,
                      border: '1px solid #d3adf7', fontSize: 13,
                      whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto',
                    }}>
                      {testResult.message_preview}
                    </pre>
                  </>
                ) : (
                  <Alert message={t('kw.noKeywords', 'No keywords detected. Make sure the message contains configured keywords.')} type="warning" />
                )}
              </div>
            )}
          </Card>
        </TabPane>
      </Tabs>

      {/* Add Modal */}
      <Modal title={t('kw.addKeyword', 'Add Keyword')} open={addModal} onOk={handleAdd}
        onCancel={() => { setAddModal(false); form.resetFields(); }}
        okText={t('common.create','Add')} cancelText={t('common.cancel','Cancel')}>
        <Form form={form} layout="vertical">
          <Form.Item name="keyword" label={t('kw.keyword','Keyword')} rules={[{ required: true }]}>
            <Input placeholder="t('kw.keywordPlaceholder', 'e.g. 3 bedroom, Thonglor, หาห้อง')" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="category" label={t('kw.category','Category')} initialValue="intent">
                <Select>
                  {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                    <Option key={k} value={k}>{v.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="language" label={t('kw.language','Language')} initialValue="any">
                <Select>
                  <Option value="any">🌐 {t('kw.anyLang', 'Any')}</Option>
                  <Option value="zh">🇹🇼 {t('kw.langZh','Chinese')}</Option>
                  <Option value="en">🇬🇧 English</Option>
                  <Option value="th">🇹🇭 ภาษาไทย</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="value" label={t('kw.value','Value')}>
            <Input placeholder="e.g. 3 (bedrooms), Thonglor (area), rent (intent)" />
          </Form.Item>
          <Alert message={
            <span>
              <strong>{t('kw.helpTitle', 'Guide:')}</strong><br />
              {t('kw.helpBedrooms', '• Bedrooms: fill number (0=studio, 1, 2, 3...)')}<br />
              {t('kw.helpArea', '• Area: fill standard English name (Thonglor, Asok, Phromphong...)')}<br />
              {t('kw.helpIntent', '• Intent: fill rent or buy')}
            </span>
          } type="info" />
        </Form>
      </Modal>
    </div>
  );
}
